import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import AddressQueue from './components/AddressQueue';
import AwaitingPanel, { documentKey } from './components/AwaitingPanel';
import DataHealth from './components/DataHealth';
import Analytics from './components/Analytics';
import Delivered from './components/Delivered';
import DeliveryPanel from './components/DeliveryPanel';
import DestinationFixer from './components/DestinationFixer';
import Obstacles from './components/Obstacles';
import Pomoc from './components/Pomoc';
import CorridorQueue from './components/CorridorQueue';
import FieldMonitor from './components/FieldMonitor';
import Settings from './components/Settings';
import RezervacijeEkran from './components/RezervacijeEkran';
import ProposalPanel from './components/ProposalPanel';
import TemplatePicker from './components/TemplatePicker';
import TourBuilder, { TourPoint } from './components/TourBuilder';
import {
  buildFlowGroups, CENTRALNI, flowsForDestinations as flowsForRoute, KUPAC, MPO, PREUZIMANJE,
  stavkeZaObjekte, templateSelection
} from './planiranje/tokovi';
import { EkranOperatera } from './pomoc/tekstovi';
import { ekranIzAdrese } from './ekranIzAdrese';
import { useOsvezavanje } from './osvezavanje';
import { api } from './services/api';
import { useSignedIn } from './session';
import {
  AwaitingItem,
  Delivery,
  DestinationGroup,
  Driver,
  NetworkLocation,
  Reconciliation,
  RouteTemplate,
  TourProposal,
  TourStopSelection,
  Vehicle
} from './types';

const DEFAULT_MAX_WAIT_DAYS = 1;
const DEFAULT_FILL_THRESHOLD = 60;

/**
 * Tab je IZVEDEN iz spiska ekrana koji imaju pomoc. Nije stvar discipline nego
 * tipa: nov tab bez teksta u `POMOC` ne prevodi se.
 */
type View = EkranOperatera;

interface Props {
  onSignOut: () => void;
}

export default function App({ onSignOut }: Props) {
  const signedIn = useSignedIn();
  const [view, setView] = useState<View>(() => ekranIzAdrese(window.location.search));

  /** Koliko alarma rezervacija čeka — brojka na kartici, i kad je otvorena druga. */
  const [rezervacijeCeka, setRezervacijeCeka] = useState(0);
  const osveziRezervacije = useCallback(async () => {
    try {
      setRezervacijeCeka((await api.rezervacijeBroj()).otvoreno);
    } catch {
      // Brojka nije posao: kartica i dalje radi i pokazuje sve.
    }
  }, []);
  useOsvezavanje(osveziRezervacije, 60_000);

  /**
   * Nalozi koje je operater skinuo sa ture jer tovar ne staje.
   *
   * Drži se ovde, a ne u izboru stanica, jer stanica ostaje ista — menja se
   * samo šta se na nju tovari. Ključ je „odredište|nalog".
   */
  const [excludedDocuments, setExcludedDocuments] = useState<string[]>([]);
  const [items, setItems] = useState<AwaitingItem[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [report, setReport] = useState<Reconciliation | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [locations, setLocations] = useState<NetworkLocation[]>([]);
  const [templates, setTemplates] = useState<RouteTemplate[]>([]);
  const [maxWaitDays, setMaxWaitDays] = useState(DEFAULT_MAX_WAIT_DAYS);
  const [fillThreshold, setFillThreshold] = useState(DEFAULT_FILL_THRESHOLD);
  const [selected, setSelected] = useState<string[]>([]);
  const [proposal, setProposal] = useState<TourProposal | null>(null);

  /** Redosled obilaska iz sastava ture — jedini zna gde ulaze preuzimanja. */
  const [tourSequence, setTourSequence] = useState<TourPoint[]>([]);

  /**
   * Mesta učitanog pravca, njegovim redom. Po njemu ručno dodat tok upada na
   * SVOJE mesto u redosledu obilaska, a ne na kraj spiska.
   */
  const [corridorOrder, setCorridorOrder] = useState<string[]>([]);

  /**
   * Pravac čiji predlog nije uspeo. Tura se i dalje slaže, ali BEZ MAPE — a
   * to se ranije videlo samo kao red teksta među porukama, pa je operater
   * mislio da mapa „nekad neće" (prijavljeno 27.08.2026).
   */
  const [proposalFailed, setProposalFailed] = useState<RouteTemplate | null>(null);

  /**
   * Odakle tura krece i gde se vraca. Stoji ovde, a ne u sastavu ture, jer od
   * njega zavisi i predlog i izbor tokova — a oba se rade odavde.
   *
   * Skoro uvek je centralni magacin. Menja se u vanrednoj situaciji: vozilo je
   * prenocilo u Kuli jer covek iz Kule vozi dok je magacinski na bolovanju.
   */
  const [base, setBase] = useState(CENTRALNI);

  /** Poslednji ucitan pravac, da se moze ponovo sloziti kad se polaziste promeni. */
  const [appliedTemplate, setAppliedTemplate] = useState<RouteTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.settings().catch(() => null);

      const waitDays = Number(settings?.['max_wait_days_interni']?.value);
      const effectiveWaitDays = Number.isFinite(waitDays) && waitDays > 0 ? waitDays : DEFAULT_MAX_WAIT_DAYS;
      setMaxWaitDays(effectiveWaitDays);

      const threshold = Number(settings?.['popunjenost_threshold']?.value);
      setFillThreshold(Number.isFinite(threshold) && threshold > 0 ? threshold : DEFAULT_FILL_THRESHOLD);

      // B2C ima svoj prag čekanja — kupac koji je platio čeka drugačije nego
      // maloprodajni objekat koji se dopunjava.
      const b2cWaitDays = Number(settings?.['max_wait_days_b2c']?.value);
      const effectiveB2cWaitDays = Number.isFinite(b2cWaitDays) && b2cWaitDays > 0 ? b2cWaitDays : 2;

      const [awaiting, pending, reconciliation, fleet, staff, network, routes] = await Promise.all([
        api.awaiting(effectiveWaitDays),
        api.deliveries(effectiveB2cWaitDays).catch(() => []),
        api.reconciliation().catch(() => null),
        api.vehicles(),
        api.drivers(),
        api.locations().catch(() => []),
        api.templates().catch(() => [])
      ]);

      setItems(awaiting);
      setDeliveries(pending);
      setReport(reconciliation);
      setVehicles(fleet);
      setDrivers(staff);
      setLocations(network);
      setTemplates(routes);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Roba za kupce koja ide u objekat ulazi u tokove kao i interni nalozi — staje u
  // istu stanicu sa robom za taj objekat (vidi stavkeZaObjekte).
  const groups = useMemo<DestinationGroup[]>(
    () => buildFlowGroups([...items, ...stavkeZaObjekte(deliveries)]), [items, deliveries]);

  /**
   * Učitava šablon kao polaznu tačku. Stanice bez robe se izostavljaju — nema
   * smisla voziti do mesta gde ništa ne čeka, a operater ih uvek može dodati.
   */
  /**
   * Ključ stanice iz predloga, u istom obliku u kom se pamti izbor. Prazno kad
   * stanica nema po čemu da se prepozna — takva se ne može ni dodati ni izbaciti.
   */
  const keyOfProposedStop = useCallback(
    (stop: {
      kind: string;
      documentKey: string | null;
      locationKind: string | null;
      locationCode: string | null;
      isPickup?: boolean;
    }): string | null => {
      if (stop.kind === 'CustomerAddress') {
        return stop.documentKey ? KUPAC + stop.documentKey : null;
      }

      if (!stop.locationCode) return null;

      // Radna lista označava odredište kao „MPO 003" / „MAG 021".
      const prefix = stop.locationKind === 'Warehouse' ? 'MAG' : 'MPO';
      const destination = `${prefix} ${stop.locationCode}`;

      if (stop.isPickup) return PREUZIMANJE + destination;

      // Stanica za ostavljanje živi kroz TOKOVE ka svom odredištu. Ključ je
      // izabran tok kad ga ima — po njemu mapa zna da je stanica na turi i
      // koje je po redu — a inače prvi dostupan, da klik na plavu čiodu ima
      // šta da doda. Bez robe ka odredištu stanica nema ni ključ.
      const flowKeys = groups
        .filter((g) => !g.isStale && g.destination === destination)
        .map((g) => MPO + g.flowKey);

      return flowKeys.find((key) => selected.includes(key)) ?? flowKeys[0] ?? null;
    },
    [groups, selected]
  );

  /**
   * Učitava predlog za pravac i njime puni turu.
   *
   * Predlog je polazna tačka, ne naredba — sve se i dalje dodaje, izbacuje i
   * preređuje. Zato dugme kaže „predlog", a ne „primeni".
   */
  /**
   * Gde ručno dodat tok pripada po redu pravca. Bez učitanog pravca — na
   * kraj, kao i do sada; operater tada sam daje redosled.
   */
  const flowPosition = (flowKey: string): number | undefined => {
    if (corridorOrder.length === 0) return undefined;

    const group = groups.find((g) => g.flowKey === flowKey);
    if (!group) return undefined;

    const rank = (destination: string) => {
      const at = corridorOrder.indexOf(destination);
      return at < 0 ? Number.MAX_SAFE_INTEGER : at;
    };

    const mine = rank(group.destination);

    // Ispred prvog izabranog toka koji je po pravcu DALJI od ovog.
    const before = selected.findIndex((key) => {
      if (!key.startsWith(MPO)) return false;
      const otherGroup = groups.find((g) => MPO + g.flowKey === key);
      return otherGroup ? rank(otherGroup.destination) > mine : false;
    });

    return before < 0 ? undefined : before;
  };

  /** Raste sa svakim učitavanjem pravca; sastav ture se tada sam složi. */
  const [orderSignal, setOrderSignal] = useState(0);

  const applyTemplate = async (template: RouteTemplate, polaziste: string = base) => {
    setError(null);
    setAppliedTemplate(template);

    // SVA mesta pravca, njegovim redom — ne samo stanice sa robom iz
    // magacina. Bez toga mesto za koje danas ništa ne ide iz 021 ispadne iz
    // spiska, pa se ni roba KOJA STOJI TAMO ne pokupi.
    const route = template.stops.map((s) => s.destination);
    setCorridorOrder(route);

    try {
      const built = await api.proposal(template.id, undefined, polaziste);
      setProposal(built);
      setProposalFailed(null);
      setSelected(templateSelection(built.stops, route, groups, locations, polaziste));
      setOrderSignal((n) => n + 1);
    } catch (e) {
      // Bez predloga se pravac i dalje može učitati po starom — stanice sa
      // robom. Ali mape tada nema, pa se to mora REĆI, ne prećutati.
      setError(e instanceof Error ? e.message : String(e));
      setProposal(null);
      setProposalFailed(template);
      setSelected(flowsForRoute(route, groups, locations, polaziste));
      setOrderSignal((n) => n + 1);
    }
  };

  /**
   * Menja polaziste. Kad je pravac vec ucitan, slaze se ponovo — sa drugog
   * kraja ide drugi redosled, druga preuzimanja i drugi tovar na polasku.
   */
  const changeBase = (next: string) => {
    setBase(next);
    if (appliedTemplate) void applyTemplate(appliedTemplate, next);
  };

  /**
   * Dodaje ili izbacuje stanicu.
   *
   * <paramref name="position"/> je mesto na koje stanica pripada po računici —
   * bez njega je svaka dodata stanica završavala na KRAJU ture. Zbog toga se
   * dešavalo da tura prođe kroz Apatin, ode u Sontu, pa se vrati u Apatin na
   * adrese koje je operater dodao klikom.
   */
  const toggle = (key: string, position?: number) =>
    setSelected((current) => {
      if (current.includes(key)) {
        return current.filter((k) => k !== key);
      }

      if (position === undefined || position < 0 || position > current.length) {
        return [...current, key];
      }

      const next = [...current];
      next.splice(position, 0, key);
      return next;
    });

  const toggleDocument = (flowKey: string, document: string | null) => {
    const key = documentKey(flowKey, document);

    const next = excludedDocuments.includes(key)
      ? excludedDocuments.filter((k) => k !== key)
      : [...excludedDocuments, key];

    setExcludedDocuments(next);

    // Tok kome je skinut i poslednji nalog prestaje da bude stanica.
    // Inače bi na spisku ture stajalo mesto na koje se ništa ne vozi.
    const group = groups.find((g) => g.flowKey === flowKey);
    const anythingLeft = group?.items.some(
      (item) => !next.includes(documentKey(flowKey, item.sourceDocument)));

    if (group && !anythingLeft) {
      setSelected((current) => current.filter((k) => k !== MPO + flowKey));
    }
  };

  /**
   * Prihvata predloženi redosled stanica.
   *
   * Ključevi koji u predlog nisu ušli — stanica bez šifre, ili nešto što je u
   * međuvremenu otpalo — ostaju na kraju umesto da tiho nestanu iz ture.
   */
  const reorder = (keys: string[]) =>
    setSelected((current) => [...keys, ...current.filter((k) => !keys.includes(k))]);

  // Redosled izbora je redosled obilaska, pa se stanice ne sortiraju ponovo.
  // Ključ koji više nema pokriće — roba otišla, isporuka rezervisana — tiho
  // ispada, jer bi turi dodao stanicu bez sadržaja.
  /**
   * Preuzimanja tokova za mapu: mesta gde tura uzima robu internih prenosa.
   * Sastav ture ih izvodi iz izabranih tokova; ovde se izvode isto, da mapa
   * ne cuti o stanici na kojoj vozilo stvarno staje.
   */
  const transferPickups = useMemo(() => {
    const byOrigin = new Map<string, { label: string; latitude: number; longitude: number; pieces: number }>();

    for (const key of selected) {
      if (!key.startsWith(MPO)) continue;

      const group = groups.find((g) => MPO + g.flowKey === key);
      if (!group || !group.origin || group.origin === 'MAG 021') continue;

      const match = group.origin.match(/^(MPO|MAG) (\d{3})$/);
      if (!match) continue;

      const location = locations.find(
        (l) => l.kind === (match[1] === 'MAG' ? 'Warehouse' : 'Store') && l.code === match[2]);
      if (!location?.latitude || !location.longitude) continue;

      const existing = byOrigin.get(group.origin);
      const pieces = group.items.reduce((sum, i) => sum + i.quantity, 0);

      if (existing) existing.pieces += pieces;
      else {
        byOrigin.set(group.origin, {
          label: location.city || location.name,
          latitude: location.latitude,
          longitude: location.longitude,
          pieces
        });
      }
    }

    return Array.from(byOrigin.values());
  }, [selected, groups, locations]);

  const selectedStops = useMemo<TourStopSelection[]>(
    () =>
      selected
        .map((key): TourStopSelection | null => {
          if (key.startsWith(KUPAC)) {
            const delivery = deliveries.find((d) => KUPAC + d.documentKey === key);
            return delivery ? { kind: 'kupac', key, delivery } : null;
          }

          // Preuzimanje ne postoji ni u jednoj radnoj listi — nastalo je u
          // predlogu, iz odluke odakle roba ide. Zato se traži tamo.
          if (key.startsWith(PREUZIMANJE)) {
            const stop = proposal?.stops.find((s) => keyOfProposedStop(s) === key);
            return stop ? { kind: 'preuzimanje', key, stop } : null;
          }

          const group = groups.find((g) => MPO + g.flowKey === key);
          if (!group) return null;

          // Izbačeni nalozi otpadaju ovde, na jednom mestu — pa ih ni procena
          // popunjenosti, ni redosled utovara, ni sačuvana tura ne vide. Panel
          // ih i dalje prikazuje precrtane, da operater može da se predomisli.
          const items = group.items.filter(
            (item) => !excludedDocuments.includes(documentKey(group.flowKey, item.sourceDocument)));

          // Odredište kome je skinuto sve nije stanica bez sadržaja nego stanica
          // koje nema.
          if (items.length === 0) return null;

          return {
            kind: 'mpo',
            key,
            group: {
              ...group,
              items,
              totalQuantity: items.reduce((sum, i) => sum + i.quantity, 0)
            }
          };
        })
        .filter((s): s is TourStopSelection => s !== null),
    [selected, groups, deliveries, proposal, keyOfProposedStop, excludedDocuments]
  );

  return (
    <div className="app">
      <header className="app__header">
        <div>
          <h1>Elbraco — planiranje transporta</h1>
          <p className="muted">
            {view === 'planiranje'
              && 'Interni prenosi i isporuke kupcima idu u istu turu, na istom pravcu.'}
            {view === 'adrese'
              && 'Adrese kupaca koje nisu geokodirane dovoljno sigurno da uđu u rutu.'}
            {view === 'teren'
              && 'Šta se dešava na putu i šta čeka tvoju odluku.'}
            {view === 'pravci'
              && 'Mesta kroz koja pravci prolaze. Dok nisu potvrđena, pojas se ne računa.'}
            {view === 'analitika'
              && 'Koliko tura stvarno traje, mereno po stanici — i koliko to odstupa od procene.'}
            {view === 'prepreke'
              && 'Roba i isporuke koje ne mogu u turu — i šta treba uraditi da mogu.'}
          </p>
        </div>
        <div className="app__who">
          <span className="muted">{signedIn.displayName}</span>
          <button type="button" className="link" onClick={onSignOut}>Odjavi se</button>
        </div>
        {view === 'planiranje' && (
          <button type="button" onClick={() => void load()} disabled={loading}>
            {loading ? 'Učitavam…' : 'Osveži'}
          </button>
        )}
      </header>

      <nav className="tabs">
        <button
          type="button"
          className={view === 'planiranje' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('planiranje')}
        >
          Planiranje ture
        </button>
        <button
          type="button"
          className={view === 'adrese' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('adrese')}
        >
          Adrese za potvrdu
        </button>
        <button
          type="button"
          className={view === 'teren' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('teren')}
        >
          Sa terena
        </button>
        <button
          type="button"
          className={view === 'rezervacije' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('rezervacije')}
        >
          Rezervacije za kupce
          {rezervacijeCeka > 0 && <span className="count count--ceka">{rezervacijeCeka}</span>}
        </button>
        <button
          type="button"
          className={view === 'pravci' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('pravci')}
        >
          Pravci
        </button>
        <button
          type="button"
          className={view === 'prepreke' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('prepreke')}
        >
          Ne može u turu
        </button>
        <button
          type="button"
          className={view === 'isporuceno' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('isporuceno')}
        >
          Isporučeno
        </button>
        <button
          type="button"
          className={view === 'analitika' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('analitika')}
        >
          Analitika
        </button>
        {/* Poslednja namerno: ovde se ulazi kad se nešto menja, ne svaki dan. */}
        <button
          type="button"
          className={view === 'podesavanja' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
          onClick={() => setView('podesavanja')}
        >
          Podešavanja
        </button>
      </nav>

      <Pomoc ekran={view} />

      {view === 'podesavanja' ? (
        <Settings />
      ) : view === 'prepreke' ? (
        <Obstacles />
      ) : view === 'isporuceno' ? (
        <Delivered />
      ) : view === 'analitika' ? (
        <Analytics />
      ) : view === 'pravci' ? (
        <CorridorQueue />
      ) : view === 'adrese' ? (
        <AddressQueue />
      ) : view === 'teren' ? (
        <FieldMonitor />
      ) : view === 'rezervacije' ? (
        <RezervacijeEkran />
      ) : (
        <>
          {error && <div className="health health--bad">{error}</div>}
          <DataHealth report={report} />

          {/* Predlog stoji preko cele širine, iznad radnih lista. U desnoj
              koloni od 420 px mapa nema prostora — čiode se preklapaju i
              oblik ture se ne vidi, a to je jedino zbog čega mapa i postoji. */}
          {proposalFailed && (
            <div className="health health--bad">
              <strong>Mapa nije učitana</strong> za pravac „{proposalFailed.name}".
              Tura se može složiti i bez nje — spisak stanica levo je ispravan —
              ali putanja se ne crta i predlozi „usput" se ne nude.
              {' '}
              <button
                type="button"
                className="link"
                onClick={() => void applyTemplate(proposalFailed)}
              >
                Pokušaj ponovo
              </button>
            </div>
          )}

          {proposal && (
            <ProposalPanel
              proposal={proposal}
              selected={selected}
              pickups={transferPickups}
              sequence={tourSequence}
              keyOf={keyOfProposedStop}
              onToggle={toggle}
              onClose={() => { setProposal(null); setCorridorOrder([]); setProposalFailed(null); }}
            />
          )}

          <div className="app__body">
            {/* Dve radne liste stoje jedna pored druge. Ranije su bile jedna
                ispod druge, pa je spisak internih prenosa gurao isporuke kupcima
                ispod pregiba — a to su dve polovine istog posla. */}
            <div className="worklists">
              <div className="worklists__col">
              <section className="panel">
                <h2>Interni prenosi</h2>
                {loading ? (
                  <p className="muted">Učitavam…</p>
                ) : (
                  <AwaitingPanel
                    groups={groups}
                    selected={selected.filter((k) => k.startsWith(MPO)).map((k) => k.slice(MPO.length))}
                    onToggle={(flowKey) => toggle(MPO + flowKey, flowPosition(flowKey))}
                    maxWaitDays={maxWaitDays}
                    excluded={excludedDocuments}
                    onToggleDocument={toggleDocument}
                    locations={locations}
                  />
                )}
              </section>

              {/* Nalozi bez odredišta stoje uz internu listu, jer su njen deo
                  koji se ne može planirati dok se ne razreši. */}
              <DestinationFixer onRecorded={() => void load()} />
              </div>

              <div className="worklists__col">
              <section className="panel">
                <h2>
                  Isporuke kupcima{' '}
                  {deliveries.length > 0 && (
                    <span className="muted">· {deliveries.filter((d) => d.canBePlanned && !d.uObjekat).length} spremno</span>
                  )}
                </h2>
                {loading ? (
                  <p className="muted">Učitavam…</p>
                ) : (
                  <DeliveryPanel
                    deliveries={deliveries}
                    selected={selected.filter((k) => k.startsWith(KUPAC)).map((k) => k.slice(KUPAC.length))}
                    onToggle={(documentKey) => toggle(KUPAC + documentKey)}
                    onPromena={() => void load()}
                  />
                )}
              </section>
              </div>
            </div>

            <section className="panel">
              <h2>
                Tura {selected.length > 0 && <span className="muted">· {selected.length} stanica</span>}
              </h2>
              <TemplatePicker templates={templates} onApply={(t) => void applyTemplate(t)} />
              <TourBuilder
                stops={selectedStops}
                base={base}
                onBaseChange={changeBase}
                routeDestinations={corridorOrder}
                onSequence={setTourSequence}
                vehicles={vehicles}
                drivers={drivers}
                locations={locations}
                fillThreshold={fillThreshold}
                orderedKeys={selected}
                orderSignal={orderSignal}
                onReorder={reorder}
                onRemove={(d) => setSelected((c) => c.filter((x) => x !== d))}
                onSaved={() => {
                  setSelected([]);
                  setProposal(null);
                  setCorridorOrder([]);
                  void load();
                }}
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}
